import React from 'react';
import {
  Box,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

export type EqualSpacingStep = {
  key: string;
  label: React.ReactNode;
  subLabel?: React.ReactNode;
  dotColor: string;
  labelColor: string;
  fontWeight?: number;
  /** Color of the connector line leading into this step (from the left). Ignored for index 0. */
  connectorColor: string;
};

type Props = {
  steps: EqualSpacingStep[];
};

const ColorConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 7,
    left: 'var(--connector-left, calc(-50% + 7px))',
    right: 'var(--connector-right, calc(50% + 7px))',
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 2,
    borderColor: `var(--connector-color, ${theme.palette.text.disabled})`,
  },
}));

const StepIcon = ({
  color,
}: {
  color: string;
}) => (
  <Box
    sx={{
      backgroundColor: color,
      borderRadius: '50%',
      height: 14,
      width: 14,
    }}
  />
);

const EqualSpacingStepper = ({ steps }: Props) => {
  const N = steps.length;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <Stepper orientation="vertical" connector={<ColorConnector />} sx={{ p: 0 }}>
        {steps.map((step) => (
          <Step key={step.key}>
            <StepLabel
              slots={{
                stepIcon: StepIcon,
              }}
              slotProps={{
                stepIcon: {
                  color: step.dotColor as any,
                },
              }}
              optional={step.subLabel !== undefined && (
                <Typography sx={{ color: step.labelColor, fontWeight: step.fontWeight ?? 400 }}>
                  {step.subLabel}
                </Typography>
              )}
              sx={{
                '& .MuiStepLabel-label': {
                  color: step.labelColor,
                  fontWeight: step.fontWeight ?? 400,
                },
              }}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  }

  return (
    <Stepper alternativeLabel connector={<ColorConnector />} sx={{ p: 0 }}>
      {steps.map((step, index) => {
        const isFirst = index === 0;
        const isLast = index === N - 1;
        const dotFraction = index / (N - 1);

        const connectorLeft = index === 1
          ? 'calc(-100% + 14px)'
          : `calc(${(((index - 1) / (N - 1) - 1) * 100).toFixed(2)}% + 7px)`;
        const connectorRight = isLast
          ? '14px'
          : `calc(${((1 - dotFraction) * 100).toFixed(2)}% + 7px)`;

        const textAlign = (() => {
          if (isFirst) return 'left';
          if (isLast) return 'right';
          return 'center';
        })();

        const stepLabelTransform = !isFirst && !isLast
          ? `translateX(calc(${(dotFraction * 100).toFixed(2)}% - 50%))`
          : undefined;

        const fontWeight = step.fontWeight ?? 400;

        return (
          <Step
            key={step.key}
            sx={{
              '--connector-color': step.connectorColor,
              '--connector-left': connectorLeft,
              '--connector-right': connectorRight,
              padding: 0,
            }}
          >
            <StepLabel
              slots={{
                stepIcon: StepIcon,
              }}
              slotProps={{
                stepIcon: {
                  color: step.dotColor as any,
                },
              }}
              optional={step.subLabel !== undefined && (
                <Typography sx={{ color: step.labelColor, fontWeight, textAlign }}>
                  {step.subLabel}
                </Typography>
              )}
              sx={{
                ...(isFirst && { alignItems: 'flex-start' }),
                ...(isLast && { alignItems: 'flex-end' }),
                ...(stepLabelTransform && { transform: stepLabelTransform }),
                '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': {
                  color: step.labelColor,
                  fontWeight,
                  mt: 1,
                  textAlign,
                },
              }}
            >
              {step.label}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
};

export default EqualSpacingStepper;
